import {
  archiveEmail,
  getEmail,
  repairNft,
  retryAiJob,
  unarchiveEmail,
} from "@/services/ai";
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { bookingEmailType, parsedContentSchema } from "./Ai";
import { Archive, ArchiveRestore, Plus, X } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/form_fields/DatePicker";
import { Input } from "@/components/ui/input";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { Loader } from "lucide-react";
import { getCollectionIdFromAlias } from "@/services/collection";
import { useAuthContext } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const EmailDetail = () => {
  const { emailId } = useParams();
  const { t } = useTranslation();

  const [email, setEmail] = useState<bookingEmailType>();
  const [isLoading, setIsLoading] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);

  const [repairing, setRepairing] = useState(false);

  const [retrying, setRetrying] = useState(false);

  const [redirecting, setRedirecting] = useState(false);

  const [collectionId, setCollectionId] = useState<string | undefined>();

  const { userId } = useAuthContext();

  const fetchEmail = async () => {
    if (!emailId) return;
    setIsLoading(true);
    try {
      const res = await getEmail(emailId);
      console.log(res);
      setEmail(res);
    } catch {
      setEmail(undefined);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (userId) {
      fetchEmail();
    }
  }, [userId]);

  const formDefaults = useMemo(() => {
    console.log(email?.encryptedParsedContent);
    return email &&
      email.encryptedParsedContent !== "No data" &&
      email.encryptedParsedContent !== "Ofuscated data"
      ? email.encryptedParsedContent
      : {
          _createdAt: undefined,
          collectionId: undefined,
          lang: undefined,
          owner: undefined,
          originalPrice: undefined,
          lockDate: undefined,
          guests: [],
          payload: {
            reference: undefined,
            checkin: undefined,
            checkout: undefined,
            board: undefined,
            rooms: [],
            extra: undefined,
          },
          bookedWithLoyaltyCode: undefined,
          reservationType: undefined,
        };
  }, [email]);

  const form = useForm<z.infer<typeof parsedContentSchema>>({
    resolver: zodResolver(parsedContentSchema),
    defaultValues: formDefaults,
  });

  const {
    fields: groupFields,
    append: appendGroup,
    remove: removeGroup,
  } = useFieldArray({
    control: form.control,
    name: "guests",
  });
  const {
    fields: roomsFields,
    append: appendRooms,
    remove: removeRooms,
  } = useFieldArray({
    control: form.control,
    name: "payload.rooms",
  });

  useEffect(() => {
    if (email) {
      // prefill formState with email data
      form.reset(formDefaults);
    }
  }, [email, form]);

  const onSubmit = async (values: z.infer<typeof parsedContentSchema>) => {
    if (!email) return;
    setRepairing(true);
    try {
      const res = await repairNft({
        emailId: email._id,
        nft: values,
      });
      if (res.type === "error") {
        throw new Error(res.message);
      }
      alert("Success");
      window.location.reload();
    } catch (e) {
      alert(e);
    } finally {
      setRepairing(false);
    }
  };

  const retry = async () => {
    if (!email) return;
    setRetrying(true);
    try {
      const res = await retryAiJob({
        emailId: email._id,
      });
      console.log(res);
      alert("Success");
      window.location.reload();
    } catch (e) {
      alert(e);
    } finally {
      setRetrying(false);
    }
  };

  const goToCollection = async () => {
    if (collectionId) {
      return window.open(`/collection/${collectionId}/taks`, "_blank");
    }
    if (!email?.hotelAlias) return alert("No alias");
    setRedirecting(true);
    try {
      let id = email.hotelAlias;
      if (
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
          email.hotelAlias
        )
      ) {
        // if alias is NOT already the id, get it from backend
        id = await getCollectionIdFromAlias(email.hotelAlias);
      }
      setCollectionId(id);
      return window.open(`/collection/${id}/taks`, "_blank");
    } catch (e) {
      alert(e);
    } finally {
      setRedirecting(false);
    }
  };

  const getDate = (value: string): Date | undefined => {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return undefined;
    }
    return date;

    // return value;
  };

  if (isLoading) return <p>Loading...</p>;
  if (!email) return <p>Error</p>;

  return (
    <div className="flex flex-col gap-4">
      {retrying || repairing || redirecting ? (
        <div className="fixed z-10 bg-black bg-opacity-30 w-full h-full top-0 left-0 flex justify-center items-center">
          <Loader className="animate-spin" />
        </div>
      ) : null}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div
            className="px-2 py-1 rounded-lg"
            style={{
              backgroundColor:
                email.status === "success"
                  ? "#9AFEE3"
                  : email.status === "error"
                  ? "#ff746c"
                  : "#ddd",
            }}
          >
            <p>#{email.status === "error" ? email.errorType : email.status}</p>
          </div>
          <p>{email.encryptedContent.headers.subject}</p>
        </div>
        <p className="text-xs text-gray-500">
          {moment(email._createdAt).format("DD/MM/YYYY hh:mm:ss")}
        </p>
      </div>
      <div className="flex flex-wrap gap-4">
        <Button variant="gradient" onClick={() => setIsFormOpen((p) => !p)}>
          {isFormOpen ? "close" : "open"} form
        </Button>
        <Button variant="gradient" onClick={retry} disabled={retrying}>
          rerun Gennaro
        </Button>
        {email.hotelAlias ? (
          <Button
            variant="gradient"
            onClick={goToCollection}
            disabled={retrying}
          >
            {t("collection.tab_bookings")}
          </Button>
        ) : null}
        <Button
          variant="gradient"
          onClick={async (event) => {
            event.stopPropagation();
            if (
              confirm(
                `Clicca conferma per ${
                  email.labels.includes("archived")
                    ? "disarchiviare"
                    : "archiviare"
                }`
              )
            ) {
              if (email.labels.includes("archived")) {
                await unarchiveEmail(email._id);
              } else {
                await archiveEmail(email._id);
              }
              fetchEmail();
              return;
            }
          }}
          className="z-10"
        >
          {email.labels.includes("archived") ? (
            <ArchiveRestore className="h-4 w-4 mr-2" />
          ) : (
            <Archive className="h-4 w-4 mr-2" />
          )}
          {email.labels.includes("archived") ? "disarchivia" : "archivia"}
        </Button>
      </div>
      <div className="flex gap-4">
        <div
          className="break-words overflow-y-auto p-2 duration-300"
          style={{
            width: isFormOpen ? "50%" : "100%",
            maxHeight: isFormOpen ? 600 : "100%",
          }}
        >
          {email.encryptedContent.text.split("\n").map((p, i) => (
            <p key={i} className="w-full ">
              {p}
            </p>
          ))}
        </div>

        {email ? (
          <div
            className="max-h-[600px] p-2 duration-300 overflow-y-auto"
            style={{
              width: isFormOpen ? "50%" : "0%",
              opacity: isFormOpen ? 1 : 0,
            }}
          >
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="collectionId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Alias</FormLabel>
                      <FormControl>
                        <Input placeholder="collectionId" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="_createdAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data di creazione</FormLabel>
                      <FormControl>
                        <div>
                          <DatePicker
                            className="w-full"
                            {...field}
                            value={getDate(field.value)}
                            onChange={(d) => field.onChange(d.toISOString())}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lang"
                  render={({ field }) => (
                    <FormItem className="lang">
                      <FormLabel>Lingua</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Scegli Lingua" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="it">IT</SelectItem>
                          <SelectItem value="en">EN</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="owner"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Proprietario</FormLabel>
                      <FormControl>
                        <Input placeholder="owner" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="originalPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prezzo</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="price"
                          {...field}
                          type="number"
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lockDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lock Date</FormLabel>
                      <FormControl>
                        <div>
                          <DatePicker
                            className="w-full"
                            {...field}
                            value={getDate(field.value)}
                            onChange={(d) => field.onChange(d.toISOString())}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex flex-col gap-2">
                  <div className="flex items-center">
                    <FormLabel>Ospiti ({form.watch().guests.length})</FormLabel>
                    <button
                      onClick={() =>
                        appendGroup({
                          firstName: "",
                          secondName: "",
                          email: "",
                        })
                      }
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {groupFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex flex-col gap-2 ml-4 p-4 bg-gray-300 w-fit rounded-lg"
                    >
                      <div className="flex justify-between w-full">
                        <p>{index + 1}</p>
                        <button onClick={() => removeGroup(index)}>
                          <X size={14} />
                        </button>
                      </div>
                      <div className="flex gap-4">
                        <FormField
                          name={`guests.${index}.firstName`}
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nome</FormLabel>
                              <FormControl>
                                <Input placeholder="first name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          name={`guests.${index}.secondName`}
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Cognome</FormLabel>
                              <FormControl>
                                <Input placeholder="second name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          name={`guests.${index}.email`}
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl>
                                <Input placeholder="email" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <FormField
                  control={form.control}
                  name="reservationType"
                  render={({ field }) => (
                    <FormItem className="reservationType">
                      <FormLabel>Tariffa</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Scegli Tariffa" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="RESELLABLE">RESELLABLE</SelectItem>
                          <SelectItem value="NON_REFUNDABLE">
                            NON_REFUNDABLE
                          </SelectItem>
                          <SelectItem value="INSURANCE">INSURANCE</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="payload.reference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Numero prenotazione</FormLabel>
                      <FormControl>
                        <Input placeholder="reference" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="payload.checkin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Checkin</FormLabel>
                      <FormControl>
                        <div>
                          <DatePicker
                            className="w-full"
                            {...field}
                            value={getDate(field.value)}
                            onChange={(d) => field.onChange(d.toISOString())}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="payload.checkout"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Checkout</FormLabel>
                      <FormControl>
                        <div>
                          <DatePicker
                            className="w-full"
                            {...field}
                            value={getDate(field.value)}
                            onChange={(d) => field.onChange(d.toISOString())}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="payload.board"
                  render={({ field }) => (
                    <FormItem className="payload.board">
                      <FormLabel>Board</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Niente" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="RO">RO</SelectItem>
                          <SelectItem value="BNB">BNB</SelectItem>
                          <SelectItem value="HB">HB</SelectItem>
                          <SelectItem value="FB">FB</SelectItem>
                          <SelectItem value="AI">AI</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="payload.extra"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Extra</FormLabel>
                      <FormControl>
                        <Input placeholder="extra" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bookedWithLoyaltyCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>bookedWithLoyaltyCode</FormLabel>
                      <FormControl>
                        <Input placeholder="bookedWithLoyaltyCode" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-2">
                  <div className="flex items-center">
                    <FormLabel>
                      Stanze ({form.watch().payload?.rooms?.length})
                    </FormLabel>
                    <button
                      onClick={() =>
                        appendRooms({
                          name: "",
                          guestsAdults: 0,
                          guestsKids: 0,
                          amenities: "",
                        })
                      }
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {roomsFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex flex-col gap-2 ml-4 p-4 bg-gray-300 w-fit rounded-lg"
                    >
                      <div className="flex justify-between w-full">
                        <p>{index + 1}</p>
                        <button onClick={() => removeRooms(index)}>
                          <X size={14} />
                        </button>
                      </div>
                      <div className="flex gap-4">
                        <FormField
                          name={`payload.rooms.${index}.name`}
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nome stanza</FormLabel>
                              <FormControl>
                                <Input placeholder="first name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          name={`payload.rooms.${index}.guestsAdults`}
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Adulti</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="second name"
                                  {...field}
                                  type="number"
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          name={`payload.rooms.${index}.guestsKids`}
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Bambini</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="email"
                                  {...field}
                                  type="number"
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          name={`payload.rooms.${index}.amenities`}
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Servizi</FormLabel>
                              <FormControl>
                                <Input placeholder="email" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="gradient" type="submit" disabled={repairing}>
                  {t("nft.create")}
                </Button>
              </form>
            </Form>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EmailDetail;
